import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import {
  PersonAddAlt1 as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from 'shared/hooks/usePageTitle';
import { passwordRecoveryApi } from 'shared/services/passwordRecoveryApi';

/**
 * Accept-invitation flow. Distinct from `/reset-password` — the URL,
 * copy, and visual treatment frame this as "first-time account setup"
 * rather than "recovering a forgotten password". Under the hood it uses
 * the same `passwordResetToken` mechanism on the user document, so the
 * BE endpoint (POST /api/auth/reset-password) is shared.
 */
export const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [checkingToken, setCheckingToken] = useState(true);
  const [success, setSuccess] = useState(false);

  usePageTitle('Aceptar invitación', 'Configure su contraseña para acceder');

  useEffect(() => {
    const tokenFromQuery = searchParams.get('token');
    if (!tokenFromQuery || tokenFromQuery.length < 10) {
      setError('Token de invitación inválido o faltante');
      setIsTokenValid(false);
      setCheckingToken(false);
      return;
    }
    setToken(tokenFromQuery);

    // Check the token against the backend right away — a dead/revoked/
    // expired invitation used to render a normal-looking password form
    // and only reveal it was invalid after the user filled it in and
    // submitted. This surfaces the same "Invitación no válida" screen
    // immediately on load instead.
    let cancelled = false;
    passwordRecoveryApi.validateResetToken(tokenFromQuery).then((res) => {
      if (cancelled) return;
      if (!res.success) {
        setError(
          'El enlace de invitación ha expirado o es inválido. Solicite a su administrador que le reenvíe la invitación.',
        );
        setIsTokenValid(false);
      }
      setCheckingToken(false);
    });
    return () => { cancelled = true; };
  }, [searchParams]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
    if (!/(?=.*[a-z])/.test(pwd))
      return 'Incluya al menos 1 minúscula, 1 mayúscula, 1 número y 1 símbolo (@$!%*?&)';
    if (!/(?=.*[A-Z])/.test(pwd))
      return 'Incluya al menos 1 minúscula, 1 mayúscula, 1 número y 1 símbolo (@$!%*?&)';
    if (!/(?=.*\d)/.test(pwd))
      return 'Incluya al menos 1 minúscula, 1 mayúscula, 1 número y 1 símbolo (@$!%*?&)';
    if (!/[@$!%*?&]/.test(pwd))
      return 'Incluya al menos 1 minúscula, 1 mayúscula, 1 número y 1 símbolo (@$!%*?&)';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pwdError = validatePassword(password);
    if (pwdError) {
      setError(pwdError);
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const data = await passwordRecoveryApi.resetPassword(token, password);
      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', {
            state: {
              message:
                'Cuenta activada. Ya puede iniciar sesión con su nueva contraseña.',
              type: 'success',
              email: data.email,
            },
          });
        }, 2500);
      } else {
        if (data.error?.includes('inválido') || data.error?.includes('expir')) {
          setError(
            'El enlace de invitación ha expirado o es inválido. Solicite a su administrador que le reenvíe la invitación.',
          );
          setIsTokenValid(false);
        } else {
          setError(data.error || 'Error al configurar la contraseña');
        }
      }
    } catch {
      setError('Error de conexión. Por favor intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingToken) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!isTokenValid) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
          }}
        >
          <Card sx={{ width: '100%', textAlign: 'center', p: 2, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px auto',
                }}
              >
                <EmailIcon sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ color: 'error.main', mb: 2 }}>
                Invitación no válida
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Solicite a su administrador que le reenvíe la invitación desde el panel
                de usuarios.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/login')}>
                Ir al inicio de sesión
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 3,
          }}
        >
          <Card sx={{ width: '100%', textAlign: 'center', p: 2, boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px auto',
                }}
              >
                <CheckCircleIcon sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ color: 'success.main', mb: 2 }}>
                ¡Cuenta activada!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Le estamos redirigiendo al inicio de sesión.
              </Typography>
              <CircularProgress size={28} />
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <Card sx={{ width: '100%', p: 2, boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PersonAddIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Bienvenido a Safety Monitor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure su contraseña para activar la cuenta.
                </Typography>
              </Box>
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
              Esta es la primera vez que ingresa. El enlace de invitación es de un solo
              uso y caduca en 7 días.
            </Alert>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoFocus
                helperText="Mínimo 8 caracteres, 1 mayúscula, 1 minúscula, 1 número y 1 símbolo (@$!%*?&)"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Confirmar contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((s) => !s)}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              ) : null}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.25 }}
              >
                {loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Activar cuenta'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AcceptInvitation;
